import {
  CartItemDesignInstance,
  DesignSize,
  CartItemDesignInstanceLogo,
  CartItemDesign,
} from "../models/CartItem";
import { useOrderActionHandler } from "../hooks/useOrderActionsHandler";
import { DesignLogo } from "../../../../core/models/entities/Design";
import { getLogoType } from "../../../../core/models/entities/BaseLogo";
import { PrimaryButton } from "../../../components/Buttons/PrimaryButton";
import { SecondaryButton } from "../../../components/Buttons/SecondaryButton";
import { useUploadDialog } from "./UploadDialog";
import { DesignInstanceVM } from "./DesignInstance.VM";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faDownload,
  faInfoCircle,
  faPlus,
  faTrash,
  faUpload,
} from "@fortawesome/free-solid-svg-icons";
import { DesignInstanceComponent } from "./DesignInstance.Component";
import { CasingOption, getCaseLabel } from "../../../shared/functions/casing";
import { useMemo, useState } from "react";
import { faCircle } from "@fortawesome/free-regular-svg-icons";
import { getDesignLogoPlacementMap } from "../functions/getDesignLogoPlacement";

interface LogoTextEditorProps {
  orderlineDesign: CartItemDesign;
  setCasing: (casing: CasingOption) => void;
  casing: CasingOption;
}

export function LogoTextEditor({
  orderlineDesign,
  casing,
  setCasing,
}: LogoTextEditorProps) {
  const {
    handleDesignInstanceUpdateStorage,
    handleDownloadTemplate,
    handleUploadDesignTemplate,
  } = useOrderActionHandler();

  // Curry
  const getCasingSetter = (casing: CasingOption) => () => setCasing(casing);

  const placementLabels = useMemo(
    () => getDesignLogoPlacementMap(orderlineDesign.design),
    [orderlineDesign.design]
  );

  const designLogos = orderlineDesign.design.designLogos ?? [];

  const normalizedInstances = useMemo(
    () =>
      orderlineDesign.instances.map((instance) => {
        const mapped = instance.logos.map((logo, logoIndex) => {
          const referenceDesignLogo = designLogos[logoIndex];
          const inferredInDesignId =
            logo.designLogoInDesignId ?? referenceDesignLogo?.inDesignId;
          const placementLabel = inferredInDesignId
            ? placementLabels.get(inferredInDesignId)
            : undefined;
          return {
            ...logo,
            designLogoInDesignId: inferredInDesignId,
            locationIdentifier:
              placementLabel ??
              logo.locationIdentifier ??
              referenceDesignLogo?.viewName ??
              "",
          };
        });
        const sorted = mapped.slice().sort((a, b) => {
          const la = (a.locationIdentifier ?? "").toUpperCase();
          const lb = (b.locationIdentifier ?? "").toUpperCase();
          const [ga, za, na] = parsePositionLabel(la);
          const [gb, zb, nb] = parsePositionLabel(lb);
          if (ga !== gb) return ga - gb;
          if (za !== zb) return za - zb;
          if (na !== zb) {
            const naNum = isNaN(na) ? Number.MAX_SAFE_INTEGER : na;
            const nbNum = isNaN(nb) ? Number.MAX_SAFE_INTEGER : nb;
            if (naNum !== nbNum) return naNum - nbNum;
          }
          return 0;
        });
        return { ...instance, logos: sorted };
      }),
    [orderlineDesign.instances, designLogos, placementLabels]
  );

  function mapLogoToLogoSpec(
    designLogo: DesignLogo
  ): CartItemDesignInstanceLogo {
    const placementLabel =
      placementLabels.get(designLogo.inDesignId) ??
      designLogo.viewName?.toUpperCase?.() ??
      "";
    return {
      logoVariant: getLogoType(designLogo.logo),
      locationIdentifier: placementLabel,
      value: "",
      logoId: designLogo.logoId,
      baseLogo: designLogo.logo,
      designLogoInDesignId: designLogo.inDesignId,
    } as CartItemDesignInstanceLogo;
  }

  function removeRow(row: number): void {
    const newDesign: CartItemDesign = {
      ...orderlineDesign,
      instances: orderlineDesign.instances.filter((_, index) => index !== row),
    };
    handleDesignInstanceUpdateStorage(newDesign);
  }

  function addRow(): void {
    const newDesign: CartItemDesign = {
      ...orderlineDesign,
      instances:
        orderlineDesign.instances?.concat({
          logos: orderlineDesign.design.designLogos.map(mapLogoToLogoSpec),
          toDelivery: 1,
          toStorage: 0,
          label: "",
          size: "M",
        }) ?? [],
    };
    handleDesignInstanceUpdateStorage({ ...newDesign });
  }

  function updateInstance(
    index: number,
    instance: CartItemDesignInstance
  ): void {
    const instances = (orderlineDesign.instances ?? []).map((inst, idx) =>
      idx === index ? instance : inst
    );

    const newDesign: CartItemDesign = {
      ...orderlineDesign,
      instances,
    };

    handleDesignInstanceUpdateStorage({ ...newDesign });
  }

  function download() {
    handleDownloadTemplate(orderlineDesign.design.customerId, [
      orderlineDesign,
    ]);
  }

  const hasNameDesign = !!orderlineDesign.instances.find((i) =>
    i.logos.find((l) => l.logoVariant === "name")
  );

  const rows = normalizedInstances?.map((instance, instanceIndex) => {
    const vm = DesignInstanceVM({
      index: instanceIndex,
      instance: instance,
      design: orderlineDesign.design,
      updateInstance,
    });
    return (
      <div className={"relative grid"} key={instanceIndex}>
        <DesignInstanceComponent {...vm} casing={casing} />
        <div
          className={
            "absolute h-full flex items-start right-20 pl-3 mr-[-25px] pt-2.5"
          }
        >
          <div
            onClick={() => removeRow(instanceIndex)}
            className={"hide hover:text-status-failed-1"}
          >
            <FontAwesomeIcon icon={faTrash} />
          </div>
        </div>
      </div>
    );
  });

  const { component: uploadDialog, open } = useUploadDialog({
    orgId: orderlineDesign.design.customerId,
    onContinue: async () => {},
    onCancel: () => {},
    onUpload: handleUploadDesignTemplate,
    onDownload: download,
  });

  const totalDesigns = orderlineDesign.instances.reduce(
    (total, instance) => instance.toDelivery + total,
    0
  );

  const hasLeftRightPlacement = Array.from(placementLabels.values()).some(
    (label) => label.includes("LEFT") || label.includes("RIGHT")
  );

  return (
    <>
      {uploadDialog}
      <div id={"text-numeric-edit"} className={"space-y-2 pb-2"}>
        <div
          id={"header"}
          className={"uppercase text-xs flex pr-8 pl-4 space-x-1 h-4"}
          style={{
            lineHeight: "18px",
            height: 18,
          }}
        >
          <div className={"w-[330px] font-bold"}>Placements </div>
          <div className={"w-[90px] font-bold"}>Sizes</div>
          <div className={"w-[90px] font-bold"}>QTY</div>
        </div>
        <div className={"overflow-y-auto overflow-x-hidden max-h-[800px]"}>
          <div className={"border rounded bg-gray-100 space-y-0.5"}>{rows}</div>
        </div>
        <div className="flex justify-between space-x-4 border rounded bg-gray-100 uppercase text-xs py-2 my-3 px-5">
          <div>Total amount</div>
          <div>{totalDesigns}</div>
        </div>
        <div className="flex justify-between space-x-4">
          <PrimaryButton className="flex-1 px-4" onClick={addRow}>
            {" "}
            <FontAwesomeIcon icon={faPlus} />
            <span className="flex-1">ADD ROW</span>
          </PrimaryButton>
          <SecondaryButton
            className="flex-1 px-4"
            onClick={open}
            disabled={false}
          >
            <FontAwesomeIcon icon={faUpload} />
            <span className="flex-1">UPLOAD EXCEL FILE</span>
          </SecondaryButton>
          <SecondaryButton
            className="flex-1 px-4"
            onClick={download}
            disabled={false}
          >
            <FontAwesomeIcon icon={faDownload} />
            <span className="flex-1">DOWNLOAD TEMPLATE</span>
          </SecondaryButton>
        </div>
        {hasNameDesign && (
          <div className="flex flex-row flex-1 gap-x-4 absolute pt-2">
            <CasingCheckbox
              onChange={getCasingSetter(CasingOption.Uppercase)}
              checked={casing === CasingOption.Uppercase}
              casing={CasingOption.Uppercase}
            />
            <CasingCheckbox
              onChange={getCasingSetter(CasingOption.UserDefined)}
              checked={casing === CasingOption.UserDefined}
              casing={CasingOption.UserDefined}
            />
            <CasingCheckbox
              onChange={getCasingSetter(CasingOption.Capitalized)}
              checked={casing === CasingOption.Capitalized}
              casing={CasingOption.Capitalized}
            />
            <CasingCheckbox
              onChange={getCasingSetter(CasingOption.Lowercase)}
              checked={casing === CasingOption.Lowercase}
              casing={CasingOption.Lowercase}
            />
          </div>
        )}
      </div>
    </>
  );
}

function parsePositionLabel(label: string): [number, number, number] {
  const groups = ["FRONT", "BACK", "LEFT SIDE", "RIGHT SIDE"];
  const zones = ["LEFT", "MIDDLE", "RIGHT"];
  const parts = label.trim().split(/\s+/);
  const first = parts[0] ?? "";
  let groupRank = groups.indexOf(first);
  if (groupRank < 0) groupRank = groups.length;
  let zoneRank = zones.indexOf(parts[1] ?? "");
  if (zoneRank < 0) zoneRank = zones.length;
  const last = parts[parts.length - 1] ?? "";
  const num = /^[0-9]+$/.test(last) ? parseInt(last, 10) : Number.NaN;
  return [groupRank, zoneRank, num];
}

interface CasingCheckboxProps {
  casing: CasingOption;
  onChange: (checked: boolean) => void;
  checked: boolean;
}
const CasingCheckbox = ({ casing, checked, onChange }: CasingCheckboxProps) => {
  const toggle = () => {
    onChange(!checked);
  };
  return (
    <div className="flex flex-col items-center">
      <div className="text-sm select-none">{getCaseLabel(casing)}</div>
      <div onClick={toggle} className="cursor-pointer">
        {checked ? (
          <FontAwesomeIcon size="xl" color="#7BBB58" icon={faCheckCircle} />
        ) : (
          <FontAwesomeIcon size="xl" color="#EEEEEE" icon={faCircle} />
        )}
      </div>
    </div>
  );
};
